import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import type { ScrewForSettings } from "../../../http/deckDesigner/types/screws";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";

export type ScrewFieldName = "connectorSubstructureScrew" | "connectorBoardScrew" | "supportScrew";

const getShortestScrew = (screws: ScrewForSettings[]): ScrewForSettings | null =>
  screws.length
    ? screws.reduce((shortestScrew, screw) => (screw.length < shortestScrew.length ? screw : shortestScrew))
    : null;

// The screw fields are derived state (their selects are disabled): this keeps one on the shortest of
// its compatible screws while it's shown, and empty otherwise. It's the field's only writer, so a
// change that keeps the same suggestion doesn't write the field at all.
export const useSuggestedScrew = (
  name: ScrewFieldName,
  screw: ScrewForSettings | null,
  compatibleScrews: ScrewForSettings[] | undefined,
  isShown: boolean,
  shouldDirty = false,
): void => {
  const { setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  const screwId = screw?.item.id ?? null;

  useEffect(() => {
    // The screws aren't loaded yet: a saved configuration keeps its screw until they are, instead of
    // losing it in between.
    if (isShown && !compatibleScrews) {
      return;
    }

    const nextScrew = isShown && compatibleScrews ? getShortestScrew(compatibleScrews) : null;

    if ((nextScrew?.item.id ?? null) !== screwId) {
      setValue(name, nextScrew, { shouldDirty });
    }
  }, [compatibleScrews, isShown, name, screwId, setValue, shouldDirty]);
};
